import { t, translateMessage } from "../i18n";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { loadRememberPreference } from "../storage";
import { LanguageSelector } from "./LanguageSelector";

type Props = {
  busy: boolean;
  message: string;
  onLogin: (input: {
    businessSlug: string;
    username: string;
    password: string;
    remember: boolean;
  }) => Promise<void>;
};

export function LoginScreen({ busy, message, onLogin }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(loadRememberPreference);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password || busy) {
      return;
    }
    try {
      await onLogin({ businessSlug: "", username, password, remember });
    } catch {
      // The parent displays the server-provided error.
    }
  }

  return (
    <main className="login-screen">
      <section className="login-panel" aria-labelledby="login-title">
        <img className="login-logo" src="/icons/icon-192.png" alt="" />
        <div>
          <h1 id="login-title">EasyOrders</h1>
          <p className="login-subtitle">{t("Σύνδεση προσωπικού")}</p>
        </div>
        <LanguageSelector compact />
        <form onSubmit={submit} className="login-form">
          <label>
            <span>{t("Όνομα χρήστη")}</span>
            <input
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          <label>
            <span>{t("Κωδικός πρόσβασης")}</span>
            <span className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <button
                type="button"
                className="icon-button password-toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
                title={showPassword ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </span>
          </label>
          <label className="remember-option">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
            />
            <span>{t("Να με θυμάσαι")}</span>
          </label>
          {message ? <div className="form-message" role="alert">{translateMessage(message)}</div> : null}
          <button className="primary-command login-command" type="submit" disabled={busy}>
            <LogIn />
            <span>{busy ? t("Σύνδεση...") : t("Σύνδεση")}</span>
          </button>
        </form>
      </section>
    </main>
  );
}
